import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const WEEK_DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const DAY_LABELS = { Mon:"Monday", Tue:"Tuesday", Wed:"Wednesday", Thu:"Thursday", Fri:"Friday", Sat:"Saturday", Sun:"Sunday" };
const SLOT_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const SLOT_GRID = (() => {
  const out = [];
  for (let h = 8; h <= 21; h++) {
    out.push(`${String(h).padStart(2,"0")}:00`);
    out.push(`${String(h).padStart(2,"0")}:30`);
  }
  return out;
})();

export default function SlotsPanel() {
  const [days, setDays] = useState(WEEK_DAYS.map((d) => ({ day_of_week: d, slots: [] })));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [customByDay, setCustomByDay] = useState({});

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/booking-slots"); if (data?.days) setDays(data.days); }
    catch (e) { toast.error(getErrorDetail(e)); }
  })(); }, []);

  function toggle(day, slot) {
    setDays((prev) => prev.map((d) => {
      if (d.day_of_week !== day) return d;
      const has = d.slots.includes(slot);
      const next = has ? d.slots.filter(s => s !== slot) : [...d.slots, slot].sort();
      return { ...d, slots: next };
    }));
    setDirty(true);
  }

  function addCustom(day) {
    const raw = (customByDay[day] || "").trim();
    if (!SLOT_TIME_RE.test(raw)) { toast.error("Use HH:MM, e.g. 13:45"); return; }
    setDays((prev) => prev.map((d) => {
      if (d.day_of_week !== day || d.slots.includes(raw)) return d;
      return { ...d, slots: [...d.slots, raw].sort() };
    }));
    setCustomByDay({ ...customByDay, [day]: "" });
    setDirty(true);
  }

  function clearDay(day) {
    setDays((prev) => prev.map((d) => d.day_of_week === day ? { ...d, slots: [] } : d));
    setDirty(true);
  }

  function copyFromPrevious(day) {
    const idx = WEEK_DAYS.indexOf(day);
    if (idx <= 0) return;
    const prevSlots = days.find(d => d.day_of_week === WEEK_DAYS[idx - 1])?.slots || [];
    setDays((prev) => prev.map((d) => d.day_of_week === day ? { ...d, slots: [...prevSlots] } : d));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/booking-slots", { days });
      if (data?.days) setDays(data.days);
      toast.success("Slots updated. Visitors see the new times immediately.");
      setDirty(false);
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="admin-slots-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Available booking slots per weekday</h3>
        <Button onClick={save} disabled={!dirty || saving} data-testid="admin-slots-save" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
          {saving ? "…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="space-y-3">
        {days.map((d, i) => (
          <div key={d.day_of_week} data-testid={`admin-slot-day-${d.day_of_week}`} className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{d.day_of_week}</div>
                <div className="font-display text-xl tracking-tight font-medium">{DAY_LABELS[d.day_of_week]}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">{d.slots.length} slot{d.slots.length === 1 ? "" : "s"}</span>
                {i > 0 && (
                  <Button size="sm" variant="ghost" onClick={()=>copyFromPrevious(d.day_of_week)} data-testid={`admin-slot-copy-${d.day_of_week}`} className="text-xs h-7">Copy {WEEK_DAYS[i-1]}</Button>
                )}
                {d.slots.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={()=>clearDay(d.day_of_week)} data-testid={`admin-slot-clear-${d.day_of_week}`} className="text-xs h-7 text-[hsl(var(--destructive))]">Clear day</Button>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {SLOT_GRID.map((slot) => {
                const on = d.slots.includes(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={()=>toggle(d.day_of_week, slot)}
                    data-testid={`admin-slot-chip-${d.day_of_week}-${slot}`}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${on ? 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))]' : 'bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--foreground))]/40 hover:text-[hsl(var(--foreground))]'}`}
                  >{slot}</button>
                );
              })}
              {d.slots.filter(s => !SLOT_GRID.includes(s)).map((slot) => (
                <button
                  key={`custom-${slot}`}
                  type="button"
                  onClick={()=>toggle(d.day_of_week, slot)}
                  data-testid={`admin-slot-chip-${d.day_of_week}-${slot}`}
                  className="px-2.5 py-1 text-xs rounded-full border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                >{slot}<span className="ml-1">·</span></button>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-2 max-w-xs">
              <Input
                placeholder="HH:MM"
                value={customByDay[d.day_of_week] || ""}
                onChange={(e)=>setCustomByDay({ ...customByDay, [d.day_of_week]: e.target.value })}
                data-testid={`admin-slot-custom-input-${d.day_of_week}`}
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={()=>addCustom(d.day_of_week)} data-testid={`admin-slot-custom-add-${d.day_of_week}`} className="rounded-full h-8 text-xs whitespace-nowrap">Add custom time</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-4 text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-3">
        <span className="text-[hsl(var(--primary))]">·</span>
        <span>Tap a chip to switch a slot ON / OFF for that weekday. Use &quot;Copy [prev day]&quot; to clone weekday hours, then tweak. Changes go live on the booking form the moment you press <span className="font-medium text-[hsl(var(--foreground))]">Save changes</span>.</span>
      </div>
    </div>
  );
}
